import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { pageDetails } from '../Recoil/ActiveEvents';
import { CalendarIcon, MapPinIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

const Events = () => {

    const [responseData, setResponseData] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currEid, setCurrEid] = useRecoilState(pageDetails);
    
    useEffect(() => {
      let eventURI = process.env.REACT_APP_API_URL +"/Events/GetCompletedEvents";
      fetch(eventURI , {method: "GET"})
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          let events = !!data.EventsType ? data.EventsType : []
          setResponseData(events);
          setLoading(false);
        })
        .catch(error => {
          setError(error.message);
          setLoading(false);
        });
    }, []);
    
    const setEventId = (item) => {
      setCurrEid({...currEid, selectedEID:item})
    }

    if (loading) {
      return (
        <section className="section-padding bg-gradient-to-br from-neutral-50 to-neutral-100">
          <div className="container-custom">
            <div className="text-center mb-16">
              <div className="shimmer w-64 h-12 mx-auto mb-4 rounded-lg"></div>
              <div className="shimmer w-96 h-6 mx-auto rounded-lg"></div>
            </div>
            <div className="grid-modern">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="card p-6 space-y-4">
                  <div className="shimmer w-full h-48 rounded-xl"></div>
                  <div className="shimmer w-3/4 h-6 rounded"></div>
                  <div className="shimmer w-1/2 h-4 rounded"></div>
                  <div className="shimmer w-full h-16 rounded"></div>
                  <div className="shimmer w-32 h-10 rounded-lg"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    if (error) {
      return (
        <section className="section-padding bg-gradient-to-br from-neutral-50 to-neutral-100">
          <div className="container-custom text-center">
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-red-900 mb-2">Unable to Load Events</h3>
              <p className="text-red-700 mb-4">{error}</p>
              <button className="btn-primary" onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          </div>
        </section>
      );
    }

  return (
    <section className="section-padding bg-gradient-to-br from-neutral-50 to-neutral-100">
        <div className="container-custom">
            {/* Section Header */}
            <div className="text-center mb-12 animate-slide-up">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-gradient-primary mb-4">
                    Our Events
                </h2>
                <p className="text-lg md:text-xl text-neutral-600 max-w-4xl mx-auto leading-relaxed">
                    We're proud of the events we've hosted that have made a lasting impact on our community. 
                    From charity fundraisers to awareness campaigns, each event brings people together to 
                    support important causes and create meaningful change.
                </p>
            </div>
            
            {/* Events Grid */}
            {responseData.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {responseData.map((item, index) => (
                    <div 
                      key={index} 
                      className="card group overflow-hidden animate-slide-up"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      {/* Event Image */}
                      <div className="relative overflow-hidden">
                        <img  
                          className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-110" 
                          src="https://kalingarayanrunners.com/images/2474663_crop.jpg" 
                          alt="Marathon Event"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        
                        {/* Event Status Badge */}
                        <div className="absolute top-4 left-4">
                          <span className="inline-flex items-center gap-2 text-primary-500 backdrop-blur-sm text-green-800 px-4 py-2 rounded-full text-sm font-semibold border border-green-200 shadow-lg hover:bg-green-200 transition-all duration-300 group-hover:scale-105">
                            <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                            Completed
                          </span>
                        </div>
                      </div>
                      
                      {/* Event Content */}
                      <div className="p-4 space-y-3">
                        <h3 className="text-lg font-semibold text-neutral-900 group-hover:text-primary-500 transition-colors duration-300 line-clamp-2">
                          {item.EventName}
                        </h3>
                        
                        {/* Event Meta */}
                        <div className="space-y-2">
                          {item.EventLocation && (
                            <div className="flex items-center gap-2 text-neutral-600">
                              <MapPinIcon className="w-4 h-4 text-primary-500" />
                              <span className="text-sm font-normal">{item.EventLocation}</span>
                            </div>
                          )}
                          
                          {item.EventDate && (
                            <div className="flex items-center gap-2 text-neutral-600">
                              <CalendarIcon className="w-4 h-4 text-primary-500" />
                              <span className="text-sm font-normal">
                                {moment(item.EventDate).format('MMM DD, YYYY')}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Event Description */}
                        {item.EventDescription && (
                          <p className="text-neutral-600 text-sm leading-relaxed line-clamp-3">
                            {item.EventDescription}
                          </p>
                        )}
                        
                        {/* CTA Button */}
                        <div className="pt-3">
                            <Link 
                              to={"/galllery"} 
                              className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-medium px-5 py-2 rounded-xl transition-all duration-300 hover:shadow-glow-primary hover:scale-105 group/btn"
                              onClick={() => setEventId(item.EventId)}
                            >
                              <span>View Gallery</span>
                              <ArrowRightIcon className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                            </Link>
                        </div>
                      </div>
                    </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-neutral-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CalendarIcon className="w-12 h-12 text-neutral-400" />
                </div>
                <h3 className="text-xl font-medium text-neutral-700 mb-2">No Events Found</h3>
                <p className="text-neutral-500 mb-6">We're working on exciting new events. Check back soon!</p>
                <button className="btn-primary">
                  Get Notified
                </button>
              </div>
            )}

        </div>
    </section>
  );
};

export default Events;